import { SfoUiJSONSchema7 } from '../../metadata.model';

/**
 * Finds a schema property at the given path in the schema hierarchy
 * @param schema - The root schema object to search in
 * @param path - Dot-notation path to the desired property (e.g. "data.altTitle")
 * @returns The schema property if found, null otherwise
 */
export const findPropertySchema = (schema: any, path: string): SfoUiJSONSchema7 | null => {
  const paths = path.split('.');
  let current = schema;

  if (current.properties) {
    for (const part of paths) {
      if (current.properties && current.properties[part]) {
        current = current.properties[part];
      } else if (current.items && part === 'items') {
        current = current.items;
      } else {
        return null;
      }
    }
  }

  return current;
};

/**
 * Service-side resolver mirroring the resolveObjectSchema pipe: unwraps an object schema
 * from anyOf/oneOf for rendering/building nested group controls.
 */
export const resolveObjectSchemaForService = (
  schema: SfoUiJSONSchema7 | null | undefined,
): SfoUiJSONSchema7 | null => {
  if (!schema) return null;
  if (schema.properties && (schema.type === 'object' || !schema.type)) {
    return schema;
  }
  const candidates = (schema.anyOf || schema.oneOf) as (SfoUiJSONSchema7 | boolean)[] | undefined;
  if (Array.isArray(candidates)) {
    const schemas = candidates.filter(
      (opt): opt is SfoUiJSONSchema7 => typeof opt === 'object' && opt !== null,
    );
    const objectCandidate = schemas.find(
      (opt) => (opt.type === 'object' || !!opt.properties) && !!opt.properties,
    );
    if (objectCandidate) return objectCandidate;
  }
  return null;
};

/**
 * When multiple object variants are available, try to pick the one matching the provided value shape.
 * If no match is found, return the first object variant.
 */
export const pickObjectVariantForValue = (
  schema: SfoUiJSONSchema7,
  value: unknown,
): SfoUiJSONSchema7 | null => {
  const variantsMixed = (schema.anyOf || schema.oneOf) as
    | (SfoUiJSONSchema7 | boolean)[]
    | undefined;
  const variants: SfoUiJSONSchema7[] = (variantsMixed || []).filter(
    (v): v is SfoUiJSONSchema7 => typeof v === 'object' && v !== null,
  );
  const objectVariants = variants.filter((v) => v.type === 'object' || !!v.properties);
  if (!objectVariants.length) return resolveObjectSchemaForService(schema);

  if (value && typeof value === 'object') {
    const keys = new Set(Object.keys(value));
    const match = objectVariants.find((v) => {
      const required: string[] = Array.isArray(v.required) ? v.required : [];
      return required.every((rk) => keys.has(rk));
    });
    if (match) return match as SfoUiJSONSchema7;
  }
  return objectVariants[0] as SfoUiJSONSchema7;
};
